// What the web asks of the server's `share` function (sharing.js, docs/43; the kit's ShareService): joining a shared
// list, project or area from an invitation, declining one, leaving a share, and handing over invitations that waited
// for a just-verified address (`claim`). Who is in a share the server decides; the store shows what came of it
// through the listeners. Inviting, and stopping a share, stay in the apps for now (docs/47).
//
// app.todolix.app sends `/share` to the function (firebase.json), so the call stays on the page's own origin.

/** Why the server said no, as the apps tell it apart. */
export type ShareFailure =
  | "unreachable" | "needsPro" | "unverified" | "notAllowed" | "gone" | "full" | "expired" | "limit" | "failed";

export class ShareError extends Error {
  constructor(readonly failure: ShareFailure) {
    super(`share: ${failure}`);
  }
}

export interface SharingActions {
  /** Joins from an invitation; a list or project goes into `areaID` for this person (an area needs none). */
  accept(invitationID: string, areaID?: string): Promise<void>;
  decline(invitationID: string): Promise<void>;
  leave(shareID: string): Promise<void>;
  /** Invitations that waited for this person's address become theirs, once it is verified. Fails silently. */
  claim(): Promise<void>;
}

export function failureOf(status: number, reason: unknown): ShareFailure {
  switch (status) {
    case 402: return "needsPro";
    case 403: return reason === "unverified" ? "unverified" : "notAllowed";
    case 404: return "gone";
    case 409: return "full";
    case 410: return "expired";
    case 429: return "limit";
    default: return "failed";
  }
}

/** Calls `endpoint` as the signed-in person: their ID token as the bearer, App Check's token, the action as JSON. */
export function shareService(
  idToken: () => Promise<string | null>, endpoint = "/share", fetcher: typeof fetch = (...args) => fetch(...args),
  /** App Check's header (firebase.ts `appCheckHeaders`): the function refuses calls without it. */
  headers: () => Promise<Record<string, string>> = async () => ({}),
): SharingActions {
  async function call(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    const token = await idToken();
    if (!token) throw new ShareError("notAllowed");
    let response: Response;
    try {
      response = await fetcher(endpoint, {
        method: "POST",
        headers: { ...(await headers()), Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch {
      throw new ShareError("unreachable");
    }
    const answer = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (response.status === 200) return answer;
    throw new ShareError(failureOf(response.status, answer.reason));
  }
  return {
    accept: async (invitationID, areaID) => {
      await call({ action: "accept", invitationID, ...(areaID ? { areaID } : {}) });
    },
    decline: async (invitationID) => {
      await call({ action: "decline", invitationID });
    },
    leave: async (shareID) => {
      await call({ action: "leave", shareID });
    },
    claim: async () => {
      await call({ action: "claim" }).catch(() => undefined);
    },
  };
}
